import React, {
	useState,
	useRef,
	useMemo,
	useEffect,
} from 'react';
import {
	useDispatch,
	useSelector,
} from 'react-redux';
import classNames from 'classnames';
import Socket from '../../connection/socket';
import {
	GreenroomState as GS,
	valueof,
} from '../../stores/types/types';
import {
	baseShot,
	FEED_TYPES,
	Shot,
	ShotFeed,
} from '../../stores/reducers/shots';
import { ROOM_STATES } from '../../stores/reducers/settings';
import * as ShotsActions from '../../stores/actions/shots';
import * as keyListeners from '../../utils/keyboard-handler';
import Switch from '../general-components/switch';
import LayoutModal from '../showboard/layout-modal';
import useShots from '../../contexts/shots/provider';
import V_LAYOUTS from '../../utils/video-layout';
import { Profile } from '../../stores/reducers/invitations';
import HiddenFeedIndicator from './hidden-feed-indicator';
import DurationPicker from '../general-components/duration-picker';
import useDropArea from '../../hooks/use-dnd/use-drop-area';
import { DRAG_TYPE, DropCallback } from '../../stores/reducers/user-interface';
import AudioOnlyFeeds from './audio-only-feeds';
import changeShotLayout from '../../utils/changeShotLayout';
import useUserActions from '../../hooks/use-user-actions';
import useAutoplay from '../../hooks/use-autoplay';
import { useEffectiveShotDuration } from '../../hooks/use-effective-shot-duration';
import Portal from '../general-components/portal';
import { setModalData } from '../../stores/actions/modal';
import { SHOT_MODAL_PAGES } from '../modal/templates/shot-builder/shot-builder';
import { isVideo } from '../../utils/utils';

interface OwnProps {
	socket: Socket | null;
	// either currentShot or shotModalShot
	shotBeingEdited: Shot;
	// if these videoButtons should be editing the shot in the modal or the currentShot
	isForShotEditorModal: boolean;
	feedPosition: number;
	dropCallback: DropCallback
}

export default function VideoButtons(props: OwnProps) {
	const dispatch = useDispatch();

	const {
		socket,
		shotBeingEdited = baseShot,
		isForShotEditorModal = false,
		dropCallback,
		feedPosition,
	} = props;

	const host = useSelector((s: GS) => s.SessionReducer.host);
	const connectedUsers = useSelector((s: GS) => s.SessionReducer.connectedUsers);
	const embed = useSelector((s: GS) => s.SessionReducer.embed);
	const watcherOnly = useSelector((s: GS) => s.SessionReducer.watcher_only);
	const companionMode = useSelector((s: GS) => s.SessionReducer.companionMode);
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const media = useSelector((s: GS) => s.MediaReducer.media);
	const shotModalShot = useSelector((s: GS) => s.ShotsReducer.shotModalShot);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);
	const modalOpen = useSelector((s: GS) => s.ModalReducer.modalOpen);
	const modalData = useSelector((s: GS) => s.ModalReducer.modalData);
	const meeting = useSelector((state: GS) => state.SessionReducer.meeting);
	const useNewShotThumbs = useSelector((state: GS) => state.SettingsReducer.featureFlags.use_new_shot_thumbs);

	const { toggleAudio, toggleVideo, toggleTitles } = useUserActions();

	const {
		editShotModalShot,
		changeShotDuration,
		currentShot,
		hiddenTwoUpUserProfile,
		getShotDuration,
	} = useShots();

	const {
		updateShotAutoplay,
		getIsDurationInputDisabled,
	} = useAutoplay();

	const {
		shot_data: {
			shotFeeds,
			forcedLayout,
			videoTitlesOn,
			autoDuration,
		},
	} = shotBeingEdited;

	const shotDuration = useEffectiveShotDuration(shotBeingEdited);
	const currentShotDuration = useEffectiveShotDuration(currentShot);

	const isAutoDurationShot = useMemo(() => {
		if (isForShotEditorModal && shotFeeds.length === 1 && shotFeeds[0].media) {
			const mediaItem = media.find((m) => m.id === shotFeeds[0].media);
			return !!mediaItem
				&& (!mediaItem?.num_pages || mediaItem?.num_pages === 1)
				&& isVideo(mediaItem.images)
				&& !!mediaItem.media_data
				&& !(mediaItem.media_data.loop ?? false);
		}
		return autoDuration;
	}, [autoDuration, isForShotEditorModal, media, shotFeeds]);

	const [
		isDurationInputDisabled,
		durationTooltipMessage,
	] = getIsDurationInputDisabled({
		...shotBeingEdited,
		shot_data: {
			...shotBeingEdited.shot_data,
			autoDuration: isAutoDurationShot,
		},
	});

	const [layoutModalOpen, setLayoutModalOpen] = useState<boolean>(false);
	const [editDuration, setEditDuration] = useState<number>(0);
	const durationRef = useRef<HTMLInputElement | null>(null);

	const toggleAllTitles = () => {
		if (!token) return;

		const newValue = !shotBeingEdited.shot_data.videoTitlesOn;
		const editedShot = {
			...shotBeingEdited,
			shot_data: {
				...shotBeingEdited.shot_data,
				videoTitlesOn: newValue,
			},
		};

		if (isForShotEditorModal) {
			editShotModalShot(editedShot, socket);
		} else {
			dispatch(ShotsActions.editShot(token, editedShot, meeting?.meeting_id, meeting?.channel, useNewShotThumbs));
		}

		socket?.sendMessage('toggle-all-titles', [[0], currentShot.shot_id]);
	};

	const shouldShowExtraFeedFor2Up = useMemo(() => {
		// we have a two up layout that has one feed behind the other
		// when this layout is selected, there is a special droppable area that appears
		// dropping in this area should set the feed as the first feed position, the one behind
		if (embed || !host) { return false; }

		if (roomState === ROOM_STATES.SHOWTIME) {
			if (
				isForShotEditorModal
				&& shotModalShot?.shot_data?.forcedLayout === V_LAYOUTS.TWO.FULL_TWO
			) {
				return true;
			}
		} else if (roomState === ROOM_STATES.EDITING) {
			if (forcedLayout === V_LAYOUTS.TWO.FULL_TWO) {
				return true;
			}
		}

		return false;
	}, [embed, forcedLayout, host, isForShotEditorModal, roomState, shotModalShot]);

	const updateShotDurationCB = (seconds:number) => {
		changeShotDuration(seconds, shotBeingEdited, durationRef, isForShotEditorModal, socket);

		// Change the timer duration when a user updates the shot duration
		updateShotAutoplay(
			shotModalShot,
			seconds - editDuration,
		);
	};

	const hiddenTwoUpProfile = useMemo(
		(): Profile | null => hiddenTwoUpUserProfile(shotFeeds),

		// This should update when a user changes their info
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[connectedUsers, shotFeeds],
	);

	const editLayout = async (shot: Shot, layout: string, itemCount: number) => {
		if (!token) return;

		const editedShot = changeShotLayout(shot, layout, itemCount);
		if (editedShot) {
			if (isForShotEditorModal && socket) {
				editShotModalShot(editedShot, socket);
			} else {
				dispatch(ShotsActions.editShot(token, editedShot, meeting?.meeting_id, meeting?.channel, useNewShotThumbs));
			}
		}
	};

	const handleEditLayout = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		if (!isForShotEditorModal) {
			setLayoutModalOpen(true);
		} else {
			dispatch(setModalData({
				...modalData,
				modalPage: SHOT_MODAL_PAGES.LAYOUT,
			}));
		}
	};

	const hiddenTwoUpProfileName = useMemo((): string => {
		const firstName = hiddenTwoUpProfile?.first_name;
		const lastName = hiddenTwoUpProfile?.last_name;
		const email = hiddenTwoUpProfile?.email;

		if (firstName && lastName) {
			return `${firstName} ${lastName}`;
		} if (firstName) {
			return firstName;
		} if (lastName) {
			return lastName;
		} if (email) {
			return email;
		}

		return 'Presenter';
	}, [hiddenTwoUpProfile]);

	const hiddenFeedIndicator = useMemo((): JSX.Element => {
		const hiddenTwoUpShot: ShotFeed | undefined = shotFeeds.find(
			(feed) => feed.position === 0 && forcedLayout === V_LAYOUTS.TWO.FULL_TWO,
		);
		const mediaItem = media.find(
			(m) => m.id && m.id === hiddenTwoUpShot?.media,
		);
		const title = hiddenTwoUpShot?.feedType === FEED_TYPES.MEDIA
			? mediaItem?.original_name
			: hiddenTwoUpProfileName;

		if (hiddenTwoUpShot) {
			const feedType = hiddenTwoUpShot.feedType as valueof<typeof FEED_TYPES>;
			const mediaItemImagesThumb = mediaItem?.media_data.thumbnail_map[
				mediaItem.media_data.image_array[0]
			];

			return (
				<HiddenFeedIndicator
					feedType={feedType}
					imageUrl={mediaItemImagesThumb}
					title={title}
					userProfile={hiddenTwoUpProfile}
				/>
			);
		}

		return <></>;
	}, [
		forcedLayout,
		hiddenTwoUpProfile,
		hiddenTwoUpProfileName,
		media,
		shotFeeds,
	]);

	const { isOver, dropRef } = useDropArea({
		accepts: DRAG_TYPE.FEEDS,
		dropCallback,
		dropAreaData: { feedPosition },
	});

	useEffect(() => {
		const getAutoDuration = async (feed: number) => {
			setEditDuration(await getShotDuration(feed));
		};
		if (isAutoDurationShot && shotFeeds[0].media) {
			getAutoDuration(shotFeeds[0].media);
		} else if (currentShot && !isForShotEditorModal) {
			setEditDuration(currentShotDuration || 0);
		} else if (isForShotEditorModal) {
			setEditDuration(shotDuration || 0);
		}
	}, [
		isAutoDurationShot,
		currentShotDuration,
		currentShot.shot_data.shotFeeds,
		shotDuration,
		shotFeeds,
		media,
	]);

	keyListeners.useKey(keyListeners.KEYS.KEY_V, () => {
		if (!watcherOnly && !companionMode) {
			toggleVideo();
		}
	});

	keyListeners.useKey(keyListeners.KEYS.KEY_M, () => {
		if (!watcherOnly && !companionMode) {
			toggleAudio();
		}
	});

	keyListeners.useKey(keyListeners.KEYS.KEY_T, () => {
		if (host && (!modalOpen || isForShotEditorModal)) {
			toggleTitles();
		}
	});

	const isFullscreenOneUp = useMemo((): boolean => {
		if (isForShotEditorModal) {
			return shotModalShot?.shot_data?.forcedLayout === V_LAYOUTS.ONE.FULL;
		}

		return forcedLayout === V_LAYOUTS.ONE.FULL;
	}, [forcedLayout, isForShotEditorModal, shotModalShot]);

	const hasPresenter = useMemo(
		() => shotBeingEdited.shot_data.shotFeeds.some(
			(s: ShotFeed) => ((s.presenter || s.onsiteLocationId) && !s.audioOnly),
		),
		[shotBeingEdited.shot_data.shotFeeds],
	);

	return (
		<>
			{!embed && host && (roomState === ROOM_STATES.EDITING || isForShotEditorModal)
				&& <div className={classNames(
					'lower-setup-button-container video-buttons',
					{ 'in-modal': isForShotEditorModal },
				)}>
					{!embed && (
						<>
							<AudioOnlyFeeds isForShotEditorModal={isForShotEditorModal} socket={socket} />
							{!isForShotEditorModal && (
								<div id="portal-unmute-snackbar-2" />
							)}
						</>
					)}
					<div className='setup-button-container'>
						<button
							className="setup-button layout"
							onClick={handleEditLayout}
						>
							<span>
								{`${isFullscreenOneUp ? 'Add' : 'Edit'} Layout`}
							</span>
						</button>
						<div className={classNames('setup-button titles', { collapsed: !hasPresenter })}>
							{hasPresenter && <>
								<span>Titles</span>
								<Switch
									on={videoTitlesOn}
									onChange={toggleAllTitles}
									className={'green'}
								/>
							</>}
						</div>
						<label className="duration-label">Duration</label>
						<DurationPicker
							initialDuration={editDuration}
							rawDuration={shotBeingEdited.shot_data.shotDuration || 0}
							getDurationCB={updateShotDurationCB}
							isInputDisabled={isDurationInputDisabled}
							tooltipMessage={durationTooltipMessage}
						/>
					</div>
				</div>
			}

			{shouldShowExtraFeedFor2Up && <div
				className={classNames('extra-droppable-area', { isOver })}
				ref={dropRef}
			>
				{hiddenFeedIndicator}
			</div>}

			{layoutModalOpen && (
				<Portal targetId="layout-modal-portal">
					<LayoutModal
						open={layoutModalOpen}
						close={() => setLayoutModalOpen(false)}
						onApply={(
							layout: string,
							itemCount: number,
						) => editLayout(shotBeingEdited, layout, itemCount)}
						currentLayout={forcedLayout}
					/>
				</Portal>
			)}
		</>
	);
}
